import { createContext, useContext, useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { type Brand, type User } from '@/types/user';
import { domainMeta } from '@/utils/domain-meta';

type AuthContextType = {
  user?: User | null;
  account: Brand | null;
  brandLogoUrl: string | null;
  isAuthenticated: boolean;
  hasSourceAccess: boolean;
  isLoading: boolean;
  isError: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading, isError } = useAuth();

  const account = useMemo(() => {
    if (!user) return null;

    return user.brands.filter(brand => brand.isAccount)[0];
  }, [user]);

  const brandLogoUrl = useMemo(() => {
    if (!user) return null;

    const brand = user.brands.filter(brand => brand.default)[0];
    const logoUrl = brand?.logo_url;

    if (!logoUrl) return null;

    const { href } = new URL(logoUrl, domainMeta.monolithOrigin);
    return href;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        account,
        brandLogoUrl,
        isAuthenticated: !!user,
        hasSourceAccess:
          user?.feature_flags?.['sourcing-dashboard-user-access'] === true,
        isLoading,
        isError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context)
    throw new Error('useAuthContext must be used within <AuthProvider>');

  return context;
};
